import { z } from "zod";

export type ModerationPriority = "Positive" | "Neutral" | "Negative";

export const ConsumerResearchSentimentBreakdownSchema = z.object({
  positive: z.number(),
  negative: z.number(),
  neutral: z.number(),
});

export const ConsumerResearchCompetitorVoiceSchema = z.object({
  brand_name: z.string(),
  mention_count: z.number(),
  share_percentage: z.number(),
});

export const ConsumerResearchOverviewStatsDataSchema = z.object({
  brand_name: z.string(),
  total_mentions: z.number(),
  avg_sentiment: z.number(),
  brand_nps: z.number(),
  share_of_voice: z.number(),
  sentiment_breakdown: ConsumerResearchSentimentBreakdownSchema,
  competitor_voice: z.array(ConsumerResearchCompetitorVoiceSchema),
});

export type ConsumerResearchSentimentBreakdown = z.infer<typeof ConsumerResearchSentimentBreakdownSchema>;
export type ConsumerResearchCompetitorVoice = z.infer<typeof ConsumerResearchCompetitorVoiceSchema>;
export type ConsumerResearchOverviewStatsData = z.infer<typeof ConsumerResearchOverviewStatsDataSchema>;

const ConsumerResearchOverviewSentimentTrendItemSchema = z.object({
  month: z.string(),
  positive_count: z.number(),
  negative_count: z.number(),
});

export const ConsumerResearchOverviewSentimentTrendDataSchema = z.object({
  brand_name: z.string(),
  trend: z.array(ConsumerResearchOverviewSentimentTrendItemSchema),
});

export type ConsumerResearchOverviewSentimentTrendData = z.infer<typeof ConsumerResearchOverviewSentimentTrendDataSchema>;
export type ConsumerResearchOverviewSentimentTrendItem = z.infer<typeof ConsumerResearchOverviewSentimentTrendItemSchema>;

const ConsumerResearchOverviewWeeklyVolumeItemSchema = z.object({
  date: z.string(),
  mention_count: z.number(),
});

export const ConsumerResearchOverviewWeeklyVolumeDataSchema = z.object({
  brand_name: z.string(),
  volume: z.array(ConsumerResearchOverviewWeeklyVolumeItemSchema),
});

export type ConsumerResearchOverviewWeeklyVolumeData = z.infer<typeof ConsumerResearchOverviewWeeklyVolumeDataSchema>;
export type ConsumerResearchOverviewWeeklyVolumeItem = z.infer<typeof ConsumerResearchOverviewWeeklyVolumeItemSchema>;

const ConsumerResearchOverviewChannelMixItemSchema = z.object({
  channel: z.string(),
  mention_count: z.number(),
  percentage: z.number(),
});

export const ConsumerResearchOverviewChannelMixDataSchema = z.object({
  brand_name: z.string(),
  total_mentions: z.number(),
  channels: z.array(ConsumerResearchOverviewChannelMixItemSchema),
});

export type ConsumerResearchOverviewChannelMixData = z.infer<typeof ConsumerResearchOverviewChannelMixDataSchema>;
export type ConsumerResearchOverviewChannelMixItem = z.infer<typeof ConsumerResearchOverviewChannelMixItemSchema>;

const ConsumerResearchOverviewThemeAnalysisItemSchema = z.object({
  theme: z.string(),
  frequency: z.number(),
});

export const ConsumerResearchOverviewTopConsumerThemesDataSchema = z.object({
  brand_name: z.string(),
  positives: z.array(z.string()),
  negatives: z.array(z.string()),
  analyzed_positive_themes: z.array(ConsumerResearchOverviewThemeAnalysisItemSchema),
  analyzed_negative_themes: z.array(ConsumerResearchOverviewThemeAnalysisItemSchema),
});

export type ConsumerResearchOverviewTopConsumerThemesData = z.infer<typeof ConsumerResearchOverviewTopConsumerThemesDataSchema>;
export type ConsumerResearchOverviewThemeAnalysisItem = z.infer<typeof ConsumerResearchOverviewThemeAnalysisItemSchema>;

const ConsumerResearchCollectionReviewIntelligenceItemSchema = z.object({
  collection_name: z.string(),
  total_reviews: z.number(),
  average_rating: z.number(),
  health: z.number(),
});

export const ConsumerResearchCollectionReviewIntelligenceDataSchema = z.object({
  brand_name: z.string(),
  collections: z.array(ConsumerResearchCollectionReviewIntelligenceItemSchema),
});

export type ConsumerResearchCollectionReviewIntelligenceData = z.infer<typeof ConsumerResearchCollectionReviewIntelligenceDataSchema>;
export type ConsumerResearchCollectionReviewIntelligenceItem = z.infer<typeof ConsumerResearchCollectionReviewIntelligenceItemSchema>;

const ConsumerResearchCollectionPerformanceItemSchema = z.object({
  collection_name: z.string(),
  total_mentions: z.number(),
  nps: z.number(),
  share_of_mentions: z.number(),
  sentiment_breakdown: ConsumerResearchSentimentBreakdownSchema,
});

export const ConsumerResearchCollectionPerformanceDataSchema = z.object({
  brand_name: z.string(),
  brand_total_mentions: z.number(),
  collections: z.array(ConsumerResearchCollectionPerformanceItemSchema),
});

export type ConsumerResearchCollectionPerformanceData = z.infer<typeof ConsumerResearchCollectionPerformanceDataSchema>;
export type ConsumerResearchCollectionPerformanceItem = z.infer<typeof ConsumerResearchCollectionPerformanceItemSchema>;

const SentimentTrendEntrySchema = z.object({
  month: z.string(),
  positive_count: z.number(),
  negative_count: z.number(),
  neutral_count: z.number(),
  sentiment_score: z.number(),
});

const SentimentByChannelItemSchema = z.object({
  channel: z.string(),
  trend: z.array(SentimentTrendEntrySchema),
});

export const SentimentByChannelDataSchema = z.object({
  brand_name: z.string(),
  channels: z.array(SentimentByChannelItemSchema),
});

export type SentimentByChannelData = z.infer<typeof SentimentByChannelDataSchema>;

const GrowthMomentumItemSchema = z.object({
  channel: z.string(),
  recent_avg_volume: z.number().optional(),
  previous_avg_volume: z.number().optional(),
  current_volume: z.number().optional(),
  previous_volume: z.number().optional(),
  growth_percentage: z.number().nullable(),
});

export const GrowthMomentumDataSchema = z.object({
  brand_name: z.string(),
  channels: z.array(GrowthMomentumItemSchema),
});

export type GrowthMomentumData = z.infer<typeof GrowthMomentumDataSchema>;

const DiscussionTopicSchema = z.object({
  topic: z.string(),
  percentage: z.number(),
});

const DiscussionTopicsChannelSchema = z.object({
  channel: z.string(),
  topics: z.array(DiscussionTopicSchema),
});

export const DiscussionTopicsDataSchema = z.object({
  brand_name: z.string(),
  channels: z.array(DiscussionTopicsChannelSchema),
});

export type DiscussionTopicsData = z.infer<typeof DiscussionTopicsDataSchema>;

const ChannelHealthItemSchema = z.object({
  channel_name: z.string(),
  volume: z.number(),
  sentiment: z.number(),
  growth: z.number().nullable(),
  response_time: z.number().nullable(),
  engagement_percentage: z.number().nullable(),
  status: z.string(),
});

export const ChannelHealthDataSchema = z.object({
  brand_name: z.string(),
  channels: z.array(ChannelHealthItemSchema),
});

export type ChannelHealthData = z.infer<typeof ChannelHealthDataSchema>;
export type ChannelHealthItem = z.infer<typeof ChannelHealthItemSchema>;

export const FacebookConnectorCommentSchema = z.object({
  fb_page_id: z.string(),
  post_permalink: z.string().nullable(),
  post_id: z.string(),
  replies: z.array(z.unknown()),
  date: z.string(),
  profile_url: z.string(),
  feedback_id: z.string(),
  comment_url: z.string(),
  comment_id: z.string(),
  facebook_id: z.string(),
  likes_count: z.union([z.string(), z.number()]),
  profile_name: z.string(),
  threading_depth: z.number(),
  is_deleted: z.boolean(),
  comments_count: z.number(),
  profile_id: z.string(),
  id: z.string(),
  text: z.string(),
});


const FeedCommentSchema = z
  .object({
    post_url: z.string().nullable().optional(),
    user_name: z.string().optional(),
    sentiment: z.string().optional(),
    content: z.string().optional(),
    comment: z.string().optional(),
    text: z.string().optional(),
    channel: z.string().optional(),
    platform: z.string().optional(),
    platfrom: z.string().optional(),
    brand: z.string().optional(),
    created_at: z.string().optional(),
    timestamp: z.string().optional(),
    link: z.string().optional(),
    tags: z.array(z.string()).optional(),
    engagements: z.union([z.string(), z.number()]).optional(),
    likes: z.number().optional(),
    metadata: z.record(z.string(), z.union([z.number(), z.string(), z.null()])).optional(),
  })

const FeedPaginationSchema = z
  .object({
    page: z.number().optional(),
    limit: z.number().optional(),
    total_count: z.number().optional(),
    total_pages: z.number().optional(),
    has_next: z.boolean().optional(),
    has_previous: z.boolean().optional(),
    next_page: z.number().nullable().optional(),
    previous_page: z.number().nullable().optional(),
  })

export const FeedDataSchema = z.object({
  comments: z.array(FeedCommentSchema),
  pagination: FeedPaginationSchema.optional(),
});

export type FeedData = z.infer<typeof FeedDataSchema>;
export type FeedComment = z.infer<typeof FeedCommentSchema>;
export type FeedPagination = z.infer<typeof FeedPaginationSchema>;
